/**
 * Opportunities Page
 * Displays list of all opportunities with filters
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { geocodeCityState } from '../utils/geocoding';
import Navbar from '../components/Navbar';
import SearchInput from '../components/SearchInput';
import ProfilePostCard from '../components/ProfilePostCard';
import '../styles/colors.css';

// Looking-for tabs (matches backend looking_for values)
const LOOKING_FOR_TABS = [
  { label: 'Bands/Artists', value: 'band' },
  { label: 'Venues', value: 'venue' },
  { label: 'Photographers', value: 'photographer' },
  { label: 'Vendors', value: 'vendor' },
  { label: 'Recording Studios', value: 'studio' },
  { label: 'Musicians', value: 'musician' },
];

// Opportunity types per looking-for role (matches Backend-Enforced Fields doc)
const OPPORTUNITY_TYPES_BY_ROLE = {
  band: [
    'show opportunity',
    'recording session',
    'collaboration/feature',
    'photo shoot',
    'other',
  ],
  venue: [
    'seeking venue for show',
    'other',
  ],
  photographer: [
    'live recording',
    'live photography',
    'promo shoot',
    'album art',
    'music video',
    'other',
  ],
  vendor: [
    'seeking vendor for event',
    'seeking merch collaboration',
    'other',
  ],
  studio: [
    'recording project',
    'mixing/mastering',
    'distribution',
    'other',
  ],
};

const VENUE_TYPES = ['bar', 'restaurant', 'club', 'diy', 'theater', 'house', 'other'];

const OpportunitiesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).searchQuery || '' : '';
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).activeTab || '' : '';
  });
  const [selectedVenueTypes, setSelectedVenueTypes] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).selectedVenueTypes || [] : [];
  });
  const [startDate, setStartDate] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).startDate || '' : '';
  });
  const [endDate, setEndDate] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).endDate || '' : '';
  });
  const [compensation, setCompensation] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).compensation || 'either' : 'either';
  });
 const [locationCity, setLocationCity] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    if (saved) return JSON.parse(saved).locationCity || '';
    return user?.city || '';
  });
  const [locationState, setLocationState] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    if (saved) return JSON.parse(saved).locationState || '';
    return user?.state || '';
  });
  const [filterRadius, setFilterRadius] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    if (saved) return JSON.parse(saved).filterRadius || '';
    return user?.travel_distance ? String(user.travel_distance) : '';
  });
  const [sortBy, setSortBy] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).sortBy || 'recent' : 'recent';
  });
  const [locationError, setLocationError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [filterLat, setFilterLat] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).filterLat || null : null;
  });
  const [filterLon, setFilterLon] = useState(() => {
    const saved = sessionStorage.getItem('opportunitiesPage_filters');
    return saved ? JSON.parse(saved).filterLon || null : null;
  });


  // Save filters to sessionStorage
  useEffect(() => {
    const filters = {
      searchQuery,
      activeTab,
      selectedVenueTypes,
      startDate,
      endDate,
      compensation,
      locationCity,
      locationState,
      filterRadius,
      sortBy,
      filterLat,
      filterLon
    };
    sessionStorage.setItem('opportunitiesPage_filters', JSON.stringify(filters));
  }, [searchQuery, activeTab, selectedVenueTypes, startDate, endDate, 
      compensation, locationCity, locationState, filterRadius, sortBy, 
      filterLat, filterLon]);

  // Auto-apply saved location on mount
  useEffect(() => {
    const autoApplyUserLocation = async () => {
      // If we have saved lat/lon from session storage, use those
      if (filterLat && filterLon && filterRadius) {
        fetchOpportunities();
      } 
      // If user is logged in with location/travel_distance but no lat/lon yet, geocode their location
      else if (locationCity && locationState && filterRadius && !filterLat && !filterLon) {
        try {
          const result = await geocodeCityState(locationCity, locationState);
          setFilterLat(result.latitude);
          setFilterLon(result.longitude);
          await fetchOpportunities();
        } catch (err) {
          console.error('Failed to auto-geocode user location:', err);
          // Fall back to no location filter
          fetchOpportunities();
        }
      } 
      // No location data at all
      else {
        fetchOpportunities();
      }
    };
    
    autoApplyUserLocation();
  }, []);

  // When tab changes, clear opportunity type + venue type selections
  // (they're role-specific and would be invalid for the new tab)
  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);
    setSelectedVenueTypes([]);
  };

  // Auto-apply location when all 3 fields are filled
  const handleLocationBlur = async () => {
    if (locationCity && locationState && filterRadius) {
      setLocationError('');
      setLocationLoading(true);
      try {
        const result = await geocodeCityState(locationCity, locationState);
        setFilterLat(result.latitude);
        setFilterLon(result.longitude);
        await fetchOpportunities();
      } catch (err) {
        setLocationError('Location not found. Please check city and state.');
        setFilterLat(null);
        setFilterLon(null);
      } finally {
        setLocationLoading(false);
      }
    } else {
      // Clear location filter if any field is empty
      setFilterLat(null);
      setFilterLon(null);
      setLocationError('');
    }
  };

  const buildQueryParams = (lat, lon) => {
    const params = new URLSearchParams();

    if (activeTab) params.append('lookingFor', activeTab);

    
    // Venue type: only meaningful when the venue tab is active
    // Send as part of the looking-for result set — backend already filters by looking_for,
    // and we piggyback venue_type filtering on top in the future if needed.
    // For now we filter venue type client-side after the response (simpler, low volume).

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    if (compensation && compensation !== 'either') params.append('compensation', compensation);

    if (sortBy) params.append('sortBy', sortBy);

    if (lat && lon && filterRadius) {
      params.append('filterLat', lat);
      params.append('filterLon', lon);
      params.append('filterRadius', filterRadius);
    }

    return params.toString();
  };

  const fetchOpportunities = async (lat = filterLat, lon = filterLon) => {
    setLoading(true);
    setError('');
    try {
      const queryString = buildQueryParams(lat, lon);
      const url = queryString ? `/opportunities?${queryString}` : '/opportunities';
      const response = await apiClient.get(url);
      if (response.data.success) {
        let results = response.data.opportunities;

        // Client-side venue type filter (venues only, low volume)
        if (activeTab === 'venue' && selectedVenueTypes.length > 0) {
          // We'd need collaborator data to filter by venue type here —
          // for now this is a placeholder; venue type filtering will be
          // fully wired in when venue profiles are queryable via the find page.
          // Opportunities don't carry venue_type directly on the record.
        }

        setOpportunities(results);
      }
    } catch (err) {
      setError('Failed to load opportunities');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };



  const handleVenueTypeToggle = (type) => {
    setSelectedVenueTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  // Helper: Calculate distance between two lat/lon points (Haversine formula)
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

// Client-side filtering with search
  const filteredOpportunities = useMemo(() => {
    let data = [...opportunities];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter(opp => 
        opp.title?.toLowerCase().includes(query) ||
        opp.created_by_display_name?.toLowerCase().includes(query) ||
        opp.collaborator_names?.some(name => name?.toLowerCase().includes(query))
      );
    }

    // Active tab filter
    if (activeTab) {
      data = data.filter(opp => opp.looking_for?.includes(activeTab));
    }

    // Venue type filter (only when looking for venue)
    if (activeTab === 'venue' && selectedVenueTypes.length > 0) {
      data = data.filter(opp => 
        selectedVenueTypes.some(type => opp.opportunity_type?.includes(type))
      );
    }

    // Date range filter - includes opportunities with ANY overlap
    if (startDate || endDate) {
      data = data.filter(opp => {
        const oppStart = opp.opportunity_start; // Keep as string YYYY-MM-DD
        const oppEnd = opp.opportunity_end || opp.opportunity_start; // Keep as string
        
        // String comparison works for YYYY-MM-DD format
        if (startDate && endDate) {
          // Show if: opportunity starts on/before filter end AND ends on/after filter start
          return oppStart <= endDate && oppEnd >= startDate;
        } else if (startDate) {
          return oppEnd >= startDate;
        } else if (endDate) {
          return oppStart <= endDate;
        }
        return true;
      });
    }


   // Sort
    if (sortBy === 'recent') {
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'start_date') {
      data.sort((a, b) => new Date(a.opportunity_start) - new Date(b.opportunity_start));
    } else if (sortBy === 'distance' && filterLat && filterLon) {
      data.sort((a, b) => {
        if (!a.latitude || !a.longitude) return 1;
        if (!b.latitude || !b.longitude) return -1;
        const distA = getDistance(filterLat, filterLon, parseFloat(a.latitude), parseFloat(a.longitude));
        const distB = getDistance(filterLat, filterLon, parseFloat(b.latitude), parseFloat(b.longitude));
        return distA - distB;
      });
    }
    return data;
  }, [opportunities, searchQuery, activeTab, selectedVenueTypes, startDate, 
      endDate, compensation, filterLat, filterLon, filterRadius, sortBy]);


  const handleApplyFilters = async () => {
    setLocationError('');

    if (locationCity && locationState) {
      if (!filterRadius) {
        setLocationError('Please enter a distance radius when filtering by location.');
        return;
      }
      setLocationLoading(true);
      try {
        const result = await geocodeCityState(locationCity, locationState);
        setFilterLat(result.latitude);
        setFilterLon(result.longitude);
        await fetchOpportunities(result.latitude, result.longitude);
      } catch (err) {
        setLocationError(err.message);
        setFilterLat(null);
        setFilterLon(null);
      } finally {
        setLocationLoading(false);
      }
    } else {
      setFilterLat(null);
      setFilterLon(null);
      await fetchOpportunities(null, null);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveTab('');
    setSelectedVenueTypes([]);
    setStartDate('');
    setEndDate('');
    setCompensation('either');
    setLocationCity(user?.city || '');
    setLocationState(user?.state || '');
    setFilterRadius(user?.travel_distance ? String(user.travel_distance) : '');
    setSortBy('recent');
    setFilterLat(null);
    setFilterLon(null);
    setLocationError('');
    fetchOpportunities();
  };

 const activeFilterCount = () => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (activeTab) count++;
    if (selectedVenueTypes.length > 0) count++;
    if (startDate || endDate) count++;
    if (compensation !== 'either') count++;
    if (filterLat && filterLon && filterRadius) count++;
    return count;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatLookingFor = (arr) => {
    if (!arr || arr.length === 0) return '—';
    return arr.map(r => {
      const tab = LOOKING_FOR_TABS.find(t => t.value === r);
      return tab ? tab.label : r;
    }).join(', ');
  };

  const formatCompensation = (opp) => {
    if (opp.compensation === 'yes') return opp.compensation_amount || 'Paid';
    if (opp.compensation === 'no') return 'Unpaid';
    return 'TBD';
  };



  return (
    <>
      <style>{`
        .opportunities-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 140px 20px 60px;
        }

        .tabs-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
          margin-bottom: 28px;
        }

        .location-bar {
          background-color: var(--smoke-gray);
          padding: 12px 24px;
          border-radius: 8px;
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .filter-panel {
          max-width: 800px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .opportunities-container {
            padding: 125px 16px 60px 16px;
          }

          .opportunities-title {
            font-size: 1.8rem !important;
            margin-bottom: 20px;
          }

          .tabs-row {
            overflow-x: auto;
            flex-wrap: nowrap;
            justify-content: flex-start;
            margin-bottom: 0;
            padding-bottom: 16px;
            gap: 12px;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            margin-left: -16px;
            margin-right: -16px;
            padding-left: 16px;
            padding-right: 16px;
          }

          .tabs-row::-webkit-scrollbar {
            display: none;
          }

          .tabs-row button {
            flex-shrink: 0;
            scroll-snap-align: start;
            font-size: 15px !important;
            padding: 12px 20px !important;
          }

          .location-bar {
            padding: 12px 12px 12px 0;
            margin-bottom: 16px;
            border-radius: 0;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            width: fit-content;
          }

          .location-inputs {
            display: flex;
            gap: 8px;
          }

          .location-bar input {
            font-size: 16px !important;
            padding: 8px !important;
          }

          .search-container {
            max-width: 85vw !important;
            padding-right: 16px;
          }

          .filter-panel {
            padding: 20px !important;
            margin-bottom: 20px !important;
            max-width: 90vw;
          }
        }
      `}</style>

      <div className="opportunities-container">
      <Navbar />
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 className="opportunities-title" style={{ color: 'var(--text-light)', fontSize: '2.6rem' }}>Opportunities for</h1>
      </div>

      {/* TABS - OUTSIDE FILTER PANEL */}
      <div className="tabs-row">
        <button
          onClick={() => setActiveTab('')}
          className={activeTab === '' ? 'tab tab-dark-selected' : 'tab tab-dark-unselected'}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            fontSize: '14px'
          }}
        >
          All
        </button>
        {LOOKING_FOR_TABS.map(tab => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={activeTab === tab.value ? 'tab tab-dark-selected' : 'tab tab-dark-unselected'}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              fontSize: '14px'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
        <div className="search-container" style={{ maxWidth: '600px', width: '100%' }}>
          <SearchInput
            placeholder="Search by title or name..."
            onSearch={setSearchQuery}
            initialValue={searchQuery}
          />
        </div>
      </div>

      {/* Location Bar - Outside filter panel, styled like HomePage */}
      <div className="location-bar">
        <div className="location-inputs" style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="City"
            value={locationCity}
            onChange={(e) => setLocationCity(e.target.value)}
            onBlur={handleLocationBlur}
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', flex: 1, minWidth: '60px', maxWidth: '206px' }}
          />
          <input
            type="text"
            placeholder="ST"
            value={locationState}
            onChange={(e) => setLocationState(e.target.value.toUpperCase())}
            onBlur={handleLocationBlur}
            maxLength={2}
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', width: '47px' }}
          />
          <input
            type="number"
            placeholder="Radius (mi)"
            value={filterRadius}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              if (!e.target.value) setFilterRadius('');
              else if (v >= 1 && v <= 999) setFilterRadius(String(v));
            }}
            onBlur={handleLocationBlur}
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', width: '53px' }}
          />
        </div>
        {locationError && <div style={{ color: 'var(--accent-danger)', fontSize: '13px', width: '100%' }}>{locationError}</div>}
        {locationLoading && <div style={{ color: 'var(--text-muted)', fontSize: '13px', width: '100%' }}>Locating...</div>}
      </div>

     {/* Filter Toggle - Below location bar */}
      <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn btn-primary"
          style={{
            padding: '10px 20px',
            fontWeight: activeFilterCount() > 0 ? '700' : '600',
            opacity: activeFilterCount() > 0 ? 1 : 0.8
          }}
        >
          {showFilters ? 'Hide Filters' : `Filters${activeFilterCount() > 0 ? ` (${activeFilterCount()} active)` : ''}`}
        </button>

        {activeFilterCount() > 0 && (
          <button
            onClick={handleClearFilters}
            className="btn btn-danger"
            style={{ padding: '10px 20px' }}
          >
            Clear All
          </button>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="card filter-panel" style={{ padding: '20px', marginBottom: '25px' }}>

          {/* Sort - First item in filter */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: 'var(--text-dark)' }}>Sort</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '4px',
                border: '1px solid var(--border-light)',
                fontSize: '15px',
                backgroundColor: 'var(--bg-card)',
                color: 'var(--text-dark)',
                cursor: 'pointer'
              }}
            >
              <option value="recent">Most Recent</option>
              <option value="start_date">Start Date</option>
              {filterLat && filterLon && <option value="distance">Distance</option>}
            </select>
          </div>

{/* Date Range Filter */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: 'var(--text-dark)' }}>Date Range</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ padding: '10px 14px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '15px', color: 'var(--text-dark)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ padding: '10px 14px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '15px', color: 'var(--text-dark)' }}
                />
              </div>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '5px' }}>Filter by opportunity date range</p>
          </div>

          {/* Compensation Filter */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: 'var(--text-dark)' }}>Compensation</label>
            <select
              value={compensation}
              onChange={(e) => setCompensation(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '15px', backgroundColor: 'var(--bg-card)', color: 'var(--text-dark)', cursor: 'pointer' }}
            >
              <option value="either">Either Paid or Unpaid</option>
              <option value="paid">Paid Only</option>
              <option value="unpaid">Unpaid Only</option>
            </select>
          </div>

          <button
            onClick={handleApplyFilters}
            disabled={locationLoading}
            className="btn btn-primary"
            style={{
              padding: '10px 30px',
              cursor: locationLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              opacity: locationLoading ? 0.6 : 1
            }}
          >
            {locationLoading ? 'Locating...' : 'Apply Filters'}
          </button>
        </div>
      )}

      {/* Results Count */}
      {!loading && (
        <p style={{ color: 'var(--text-light)', marginBottom: '15px', opacity: 0.8, textAlign: 'center' }}>
          {filteredOpportunities.length === 0
            ? 'No opportunities found'
            : `${filteredOpportunities.length} opportunit${filteredOpportunities.length !== 1 ? 'ies' : 'y'} found`}
        </p>
      )}

      {/* Opportunities List */}
      {loading ? (
        <div style={{ padding: '50px', textAlign: 'center', color: 'var(--text-light)' }}>Loading opportunities...</div>
      ) : error ? (
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <p style={{ color: 'var(--accent-danger)' }}>{error}</p>
          <button onClick={() => fetchOpportunities()} className="btn btn-primary">Try Again</button>
        </div>
      ) : filteredOpportunities.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p style={{ color: 'var(--text-light)' }}>No opportunities match your filters.</p>
          {activeFilterCount() > 0 && (
            <button
              onClick={handleClearFilters}
              className="btn btn-primary"
              style={{ marginTop: '15px' }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {filteredOpportunities.map((opp) => (
            <ProfilePostCard
              key={opp.id}
              post={{ ...opp, post_type: 'opportunity' }}
            />
          ))}
        </div>
      )}
      </div>
    </>
  );
};

export default OpportunitiesPage;
